import { useEffect } from 'react';

interface KeyboardShortcutOptions {
  /** When false, the shortcut is ignored while focus is inside a text input,
   * textarea, or contenteditable element. Defaults to true — safe by default
   * since these are all modifier combos, not plain keystrokes. */
  allowInInputs?: boolean;
}

function isTextInputTarget(target: unknown): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/** Calls `onTrigger` when the user presses Cmd+<key> (macOS) or Ctrl+<key> (Windows/Linux). */
export function useKeyboardShortcut(
  key: string,
  onTrigger: () => void,
  options: KeyboardShortcutOptions = {}
): void {
  const { allowInInputs = true } = options;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const isShortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === key.toLowerCase();
      if (!isShortcut) return;
      if (!allowInInputs && isTextInputTarget(event.target)) return;

      event.preventDefault();
      onTrigger();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, onTrigger, allowInInputs]);
}
